"use client";

import { EXECUTIVE_PRACTICES } from "@/lib/demos/hiringData";
import { CheckCircle2, Shield, Sparkles, ArrowRight } from "lucide-react";

interface HiringPracticesSectionProps {
  currency: "INR" | "USD";
  onSelectPractice: (practiceId: string) => void;
}

export function HiringPracticesSection({
  currency,
  onSelectPractice,
}: HiringPracticesSectionProps) {
  const sym = currency === "USD" ? "$" : "₹";

  return (
    <section className="py-20 bg-[#0a0710] border-t border-purple-950/60 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-semibold">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Specialized Search Domains</span>
          </div>
          <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
            Tailored Executive Practices
          </h2>
          <p className="text-slate-400 text-sm sm:text-base">
            Targeted search squads led by seasoned industry partners with verifiable networks in C-Suite, AI architecture, and global enterprise sales.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {EXECUTIVE_PRACTICES.map((practice) => {
            const bounds =
              currency === "USD"
                ? practice.rolesBenchmarkUsd
                : practice.rolesBenchmarkInr;

            const formatBounds = (val: number) => {
              if (currency === "USD") return `$${val / 1000}k`;
              if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)} Cr`;
              return `₹${val / 100000}L`;
            };

            return (
              <div
                key={practice.id}
                className="p-7 rounded-2xl bg-[#140c21]/90 border border-purple-900/40 hover:border-purple-600/60 transition-all group hover:shadow-xl hover:shadow-purple-950/50 flex flex-col justify-between"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold px-2.5 py-1 rounded-md bg-purple-500/10 text-purple-300 border border-purple-500/20">
                      {practice.badge}
                    </span>
                    <span className="text-xs text-slate-400 font-mono">
                      {practice.baseFeePercentage}% Success Fee
                    </span>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-white group-hover:text-purple-300 transition-colors">
                      {practice.title}
                    </h3>
                    <p className="text-xs text-purple-400 font-medium mt-1">
                      {practice.tagline}
                    </p>
                  </div>

                  <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                    {practice.description}
                  </p>

                  <div className="space-y-2 pt-2 border-t border-purple-950/60">
                    <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                      Target Leadership Roles:
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {practice.typicalRoles.map((role, idx) => (
                        <div key={idx} className="flex items-center gap-1.5 text-xs text-slate-300">
                          <CheckCircle2 className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                          <span className="truncate">{role}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="pt-6 mt-6 border-t border-purple-900/40 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                      Compensation Band
                    </div>
                    <div className="text-sm font-bold text-white">
                      {formatBounds(bounds.min)} – {formatBounds(bounds.max)} CTC
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => onSelectPractice(practice.id)}
                    className="px-4 py-2 rounded-xl bg-purple-950 hover:bg-purple-900 text-purple-300 hover:text-white border border-purple-800/60 text-xs font-semibold flex items-center gap-1.5 transition-colors"
                  >
                    <span>Engage Practice</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
